import React, { useMemo, useState } from "react";
import { CalendarSlot, ConflictTimeslot, ProposedTimeSlot } from "../../../types";
import { Message, useConfirmMeetingTime, useUpdateProposedTimes } from "../../../store";
import EventCalendar from "./meeting-time-calendar";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";

interface MeetingTimeComponentProps {
    meetingId: string
    userId: string
    joinCode: string
    meeting: CalendarSlot,
    proposedTimes: ConflictTimeslot[],
    setMessage: (message: Message) => void
}

const proposedSlotToCalendarSlot = (slot: ConflictTimeslot | ProposedTimeSlot, meeting: CalendarSlot): CalendarSlot => {
    return {
        ...meeting,
        startTime: slot.startTime,
        endTime: slot.endTime,
    }
}

const MeetingTimeComponent: React.FC<MeetingTimeComponentProps> = ({
    meetingId,
    userId,
    joinCode,
    meeting,
    proposedTimes: pt,
    setMessage,
}) => {

    const now = new Date()
    const [proposedTimes, setProposedSlots] = useState<CalendarSlot[]>(
        pt?.map(
            item => {
                return proposedSlotToCalendarSlot(item, meeting)
            }
        )?.filter(
            item => item.creatorId === userId && item.startTime > now) || [])
    const [updated, setUpdated] = useState<boolean>(false)
    const [inProgress, setInProgress] = useState<boolean>(false)

    const proposeSuccess = (message: string) => {
        console.log(message)

        setMessage({
            message: "Updated proposed times",
            type: "success"
        })

        setInProgress(false)
    }

    const proposeFail = (error: any) => {
        console.error(error)
        const message = error?.message || "Failed to propose times"

        setMessage({
            message,
            type: "error"
        })

        setInProgress(false)
    }

    const finaliseMeetingTimeSuccess = (message: string) => {
        console.log(message)

        setMessage({
            message: "Confirmed meeting time",
            type: "success"
        })

        setInProgress(false)
    }

    const finaliseMeetingTimeFail = (error: any) => {
        console.error(error)
        const message = error?.message || "Failed to propose times"

        setMessage({
            message,
            type: "error"
        })

        setInProgress(false)
    }

    const { mutate: updateProposedTimes } = useUpdateProposedTimes(proposeSuccess, proposeFail)
    const { mutate: finaliseMeetingTime } = useConfirmMeetingTime(finaliseMeetingTimeSuccess, finaliseMeetingTimeFail)

    const onFinaliseMeetingTime = (slot: ProposedTimeSlot) => {
        setInProgress(true)

        finaliseMeetingTime({
            meetingId,
            userId,
            joinCode,
            startTime: slot.startTime,
            endTime: slot.endTime,
        })
    }

    const onAddTimeSlotDisplay = () => {
        if (!updated) {
            return
        }

        const now = new Date()
        const filtered = proposedTimes.filter(item => item.endTime.getTime() > now.getTime())

        if (filtered.length == 0) {
            return
        }

        setInProgress(true)

        updateProposedTimes({
            meetingId,
            userId,
            joinCode,
            proposedTimes: filtered,
        })
    }

    const addSlots = (slots: ProposedTimeSlot[]) => {
        setProposedSlots(slots.map(item => {
            return proposedSlotToCalendarSlot(item, meeting)
        }))
        if (!updated) {
            setUpdated(true)
        }
    }

    const otherUserSlots: CalendarSlot[] = useMemo(() => {
        if (!proposedTimes) {
            return []
        }

        return proposedTimes.map(item => {
            return proposedSlotToCalendarSlot(item, meeting)
        }).filter(item => item.creatorId !== userId)
    }, [meeting])

    return (
        <Stack spacing={1} alignItems={"center"}>
            <Typography variant="h4" component="h1" textAlign="center">
                Proposed Meeting Times for patient: {""}
            </Typography>
            <EventCalendar
                proposedSlots={proposedTimes}
                meetingSlots={otherUserSlots}
                meeting={meeting}
                userId={userId}
                meetingId={meetingId}
                joinCode={joinCode}
                onAddTimeSlotDisplay={addSlots}
                onSetMeetingTime={onFinaliseMeetingTime} />
            <Stack direction={"row"}>
                <Button
                    variant="contained"
                    onClick={onAddTimeSlotDisplay}
                    disabled={!updated || proposedTimes.length == 0 || inProgress}
                >
                    Submit Proposed Times
                </Button>
                {inProgress && <CircularProgress />}
            </Stack>
        </Stack>
    );
};

export default MeetingTimeComponent;
